import type { TriageView } from "@/lib/api";
import { relativeAge } from "@/lib/overview";
import { ACTION_LABEL, type Staged, isStale, stagedSummary } from "@/lib/triage";
import { Box, Text } from "@okshaun/components";
import { css } from "@styled-system/css";

const row = css({
  display: "flex",
  gap: "8",
  alignItems: "baseline",
  px: "8",
  py: "4",
  borderRadius: "4",
  _hover: { bg: "surface.hovered" },
});

const textButton = css({
  flex: "1",
  minW: "0",
  textAlign: "left",
  fontSize: "[13px]",
  color: "text",
  bg: "transparent",
  border: "none",
  p: "0",
  cursor: "pointer",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  outline: "none",
  _focusVisible: { textDecoration: "underline" },
});

const remove = css({
  fontFamily: "mono",
  fontSize: "12",
  color: "text.subtlest",
  bg: "transparent",
  border: "none",
  cursor: "pointer",
  px: "4",
  outline: "none",
  _hover: { color: "text.danger" },
  _focusVisible: { color: "text.danger" },
});

const sendButton = css({
  fontFamily: "mono",
  fontSize: "12",
  color: "text.inverse",
  bg: "bg.brand.bold",
  border: "none",
  borderRadius: "4",
  px: "12",
  py: "8",
  cursor: "pointer",
  outline: "none",
  _hover: { bg: "bg.brand.bold.hovered" },
  _active: { bg: "bg.brand.bold.pressed" },
  _disabled: { bg: "bg.disabled", color: "text.disabled", cursor: "not-allowed" },
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
});

export type SendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; id: string; count: number; duplicate: boolean; obsidianRunning: boolean }
  | { kind: "error"; message: string };

interface StagedTrayProps {
  staged: Staged[];
  view: TriageView | null;
  sendState: SendState;
  onSend: () => void;
  onRemove: (taskId: string) => void;
  onJump: (taskId: string) => void;
}

/** What is staged in this browser, a direct send, and what Hal still has to apply. */
export function StagedTray({ staged, view, sendState, onSend, onRemove, onJump }: StagedTrayProps) {
  const sendable = staged.filter((s) => !isStale(s, view));
  const changed = staged.length - sendable.length;
  const pendingBatches = view?.batches.filter((b) => b.status === "pending") ?? [];
  const pendingTasks = pendingBatches.reduce((n, b) => n + b.decisions, 0);
  const skipped = view
    ? Object.values(view.recent).filter((r) => r.outcome === "skipped").length
    : 0;
  const rejected = view?.batches.filter((b) => b.status === "rejected") ?? [];

  return (
    <Box as="aside" display="flex" flexDir="column" gap="16" minW="0" aria-label="Staged decisions">
      <Box display="flex" flexDir="column" gap="8">
        <Box display="flex" gap="10" alignItems="baseline">
          <Text
            textStyle="mono.xs"
            color="text.subtlest"
            letterSpacing="widest"
            textTransform="uppercase"
          >
            Staged
          </Text>
          <Text textStyle="mono.xs" color="text.subtlest">
            {staged.length === 0 ? "nothing yet" : stagedSummary(staged)}
          </Text>
        </Box>
        {staged.length > 0 && (
          <Box
            display="flex"
            flexDir="column"
            maxH="[360px]"
            overflowY="auto"
            borderTop="default"
            borderBottom="default"
            py="4"
          >
            {staged.map((s) => {
              const stale = isStale(s, view);
              return (
                <div key={s.taskId} className={row}>
                  <Text
                    textStyle="mono.xs"
                    color={stale ? "text.danger" : "text.selected"}
                    minW="[72px]"
                  >
                    {ACTION_LABEL[s.action]}
                  </Text>
                  <button
                    type="button"
                    className={textButton}
                    onClick={() => onJump(s.taskId)}
                    title={s.snapshot.text}
                  >
                    {s.snapshot.text}
                  </button>
                  {stale && (
                    <Text
                      textStyle="mono.xs"
                      color="text.danger"
                      title="The task text changed since you staged it. It will not be sent."
                    >
                      changed
                    </Text>
                  )}
                  <button
                    type="button"
                    className={remove}
                    aria-label={`Unstage ${s.snapshot.text}`}
                    onClick={() => onRemove(s.taskId)}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </Box>
        )}
        <Box display="flex" gap="8" alignItems="center" flexWrap="wrap">
          <button
            type="button"
            className={sendButton}
            disabled={sendState.kind === "sending" || sendable.length === 0}
            onClick={onSend}
          >
            {sendState.kind === "sending"
              ? "Sending…"
              : sendable.length === 0
                ? "Send to Hal"
                : `Send ${sendable.length} to Hal`}
          </button>
          <Text textStyle="mono.xs" color="text.subtlest">
            kept in this browser until you send
          </Text>
        </Box>
        {changed > 0 && (
          <Text textStyle="mono.xs" color="text.danger">
            {changed} changed since you staged {changed === 1 ? "it" : "them"} and will not be sent.
            Unstage and decide again.
          </Text>
        )}
        {sendState.kind === "sent" && (
          <Text as="output" display="block" textStyle="mono.xs" color="text.success">
            {sendState.duplicate ? "Already saved earlier" : "Saved on bee"} as {sendState.id} (
            {sendState.count} decision{sendState.count === 1 ? "" : "s"}). Hal on work applies it
            within minutes while the Mac is awake.
            {sendState.obsidianRunning
              ? ""
              : " Obsidian is not running on bee, so it syncs when Obsidian starts."}
          </Text>
        )}
        {sendState.kind === "error" && (
          <Text textStyle="mono.xs" color="text.danger" role="alert">
            Not sent: {sendState.message}. Your decisions are still staged.
          </Text>
        )}
      </Box>

      <Box display="flex" flexDir="column" gap="6" borderTop="default" pt="12">
        <Text
          textStyle="mono.xs"
          color="text.subtlest"
          letterSpacing="widest"
          textTransform="uppercase"
        >
          With Hal
        </Text>
        {pendingBatches.length === 0 ? (
          <Text textStyle="mono.xs" color="text.subtlest">
            Nothing waiting. Hal on work applies a batch within minutes while the Mac is awake.
          </Text>
        ) : (
          pendingBatches.map((b) => (
            <Text key={b.id} textStyle="mono.xs" color="text.warning">
              pending · {b.decisions} decision{b.decisions === 1 ? "" : "s"} · sent{" "}
              {b.createdAt ? relativeAge(b.createdAt) : "?"}
            </Text>
          ))
        )}
        {pendingTasks > 0 && (
          <Text textStyle="mono.xs" color="text.subtlest">
            Those tasks are out of the queue until Hal applies them.
          </Text>
        )}
        {skipped > 0 && (
          <Text textStyle="mono.xs" color="text.warning">
            Hal skipped {skipped} recently. Those cards are back in the queue, marked.
          </Text>
        )}
        {rejected.map((b) => (
          <Text key={b.id} textStyle="mono.xs" color="text.danger">
            rejected batch {b.id}: {b.rejectedWhy ?? "unreadable"}
          </Text>
        ))}
      </Box>
    </Box>
  );
}
